// Perspective Crop Tool — crop using 4 corner points to correct perspective distortion.
// Photoshop equivalent: Perspective Crop Tool (under Crop tool group).
//
// Implementation: the user places 4 corners, then commit() computes a
// perspective (projective) warp via PerspectiveCropCommand, which applies
// a full homography to every raster layer. The 4 user corners are sorted by
// their angle from the centroid to determine TL/TR/BL/BR order.

import { PerspectiveCropCommand, type Quad } from "../commands/PerspectiveCropCommand";
import { screenToDoc } from "../render/Viewport";
import type { KeyModifiers, PointerSample, Tool, ToolContext } from "./Tool";

interface Corner {
  x: number;
  y: number;
}

type DragTarget = "corner-0" | "corner-1" | "corner-2" | "corner-3" | "new-corner" | null;

interface DragState {
  target: DragTarget;
  anchorX: number;
  anchorY: number;
}

type Phase = "placing" | "adjusting";

const HANDLE_RADIUS = 8;

export class PerspectiveCropTool implements Tool {
  readonly id = "perspective-crop";
  readonly shortcut = "C";

  private corners: (Corner | null)[] = [null, null, null, null];
  private nextCorner = 0;
  private drag: DragState | null = null;
  private committed = false;
  private phase: Phase = "placing";

  onPointerDown(ctx: ToolContext, s: PointerSample): void {
    if (!ctx.doc) return;
    const p = screenToDoc(ctx.viewport, s.x, s.y);
    const px = Math.floor(p.x);
    const py = Math.floor(p.y);

    // Check if clicking near an existing corner (allowed in both phases so
    // corners can be repositioned once all 4 are placed).
    const hitCorner = this.hitTestCorner(px, py);
    if (hitCorner !== null) {
      this.drag = { target: hitCorner, anchorX: px, anchorY: py };
      return;
    }

    // Once all 4 corners are placed we're in the adjustment phase: further
    // clicks that don't hit a handle do nothing (no new corners, no commit)
    // until the user confirms with Enter or cancels with Escape.
    if (this.phase === "adjusting") return;

    // Place a new corner
    if (this.nextCorner < 4) {
      this.corners[this.nextCorner] = { x: px, y: py };
      this.nextCorner++;
      ctx.requestRender();

      // If all 4 corners placed, enter the adjustment phase instead of
      // auto-committing — corners can now be dragged before confirming.
      if (this.nextCorner === 4) {
        this.phase = "adjusting";
      }
    }
  }

  onPointerMove(ctx: ToolContext, s: PointerSample): void {
    if (!this.drag || !ctx.doc) return;
    const p = screenToDoc(ctx.viewport, s.x, s.y);
    const px = Math.floor(p.x);
    const py = Math.floor(p.y);

    const dx = px - this.drag.anchorX;
    const dy = py - this.drag.anchorY;

    if (this.drag.target && this.drag.target.startsWith("corner-")) {
      const idx = parseInt(this.drag.target.split("-")[1]!);
      const corner = this.corners[idx];
      if (corner) {
        corner.x += dx;
        corner.y += dy;
        this.drag.anchorX = px;
        this.drag.anchorY = py;
        ctx.requestRender();
      }
    }
  }

  onPointerUp(_ctx: ToolContext, _s: PointerSample): void {
    this.drag = null;
  }

  onKeyDown(ctx: ToolContext, key: string, modifiers?: KeyModifiers): void {
    if (key === "Enter" || key === "Return") {
      this.commit(ctx);
    } else if (key === "Escape") {
      this.cancel(ctx);
    }
  }

  getCorners(): (Corner | null)[] {
    return [...this.corners];
  }

  /** Current phase: "placing" until all 4 corners are down, then "adjusting". */
  getPhase(): Phase {
    return this.phase;
  }

  getProgress(): number {
    return this.nextCorner;
  }

  commit(ctx: ToolContext): void {
    if (!ctx.doc || this.nextCorner < 4) return;

    const c0 = this.corners[0];
    const c1 = this.corners[1];
    const c2 = this.corners[2];
    const c3 = this.corners[3];
    if (!c0 || !c1 || !c2 || !c3) return;

    // Sort the 4 corners into canonical order (TL, TR, BL, BR) by angle from centroid.
    const rawCorners: Corner[] = [c0, c1, c2, c3];
    const cx = (c0.x + c1.x + c2.x + c3.x) / 4;
    const cy = (c0.y + c1.y + c2.y + c3.y) / 4;

    // Sort by angle from centroid (0 = right, going counter-clockwise).
    // Reclassify as TL/TR/BL/BR.
    const sorted = [...rawCorners].sort((a, b) => {
      const angleA = Math.atan2(a.y - cy, a.x - cx);
      const angleB = Math.atan2(b.y - cy, b.x - cx);
      return angleA - angleB;
    });

    // After sorting by angle CCW from right, typical order is:
    //   right-ish (TR), up-ish (TL), left-ish (BL), down-ish (BR)
    // We pick: topLeft = min(sum x+y), topRight = min(y-x), bottomLeft = min(x-y), bottomRight = max(x+y)
    const bySum = [...rawCorners].sort((a, b) => (a.x + a.y) - (b.x + b.y));
    const byDiff = [...rawCorners].sort((a, b) => (a.y - a.x) - (b.y - b.x));
    const tl = bySum[0]!;      // min(x+y)
    const br = bySum[3]!;      // max(x+y)
    const tr = byDiff[0]!;     // min(y-x)
    const bl = byDiff[3]!;     // max(y-x)

    const quad: Quad = {
      x0: tl.x, y0: tl.y,  // TL
      x1: tr.x, y1: tr.y,  // TR
      x2: bl.x, y2: bl.y,  // BL
      x3: br.x, y3: br.y,  // BR
    };

    // Compute output dimensions from average edge lengths.
    const wTop = Math.hypot(tr.x - tl.x, tr.y - tl.y);
    const wBot = Math.hypot(br.x - bl.x, br.y - bl.y);
    const hLeft = Math.hypot(bl.x - tl.x, bl.y - tl.y);
    const hRight = Math.hypot(br.x - tr.x, br.y - tr.y);
    const outW = Math.max(1, Math.round((wTop + wBot) / 2));
    const outH = Math.max(1, Math.round((hLeft + hRight) / 2));

    ctx.emit(new PerspectiveCropCommand(quad, outW, outH));

    this.committed = true;
    this.reset();
  }

  cancel(_ctx: ToolContext): void {
    this.reset();
  }

  private reset(): void {
    this.corners = [null, null, null, null];
    this.nextCorner = 0;
    this.drag = null;
    this.committed = false;
    this.phase = "placing";
  }

  private hitTestCorner(px: number, py: number): DragTarget | null {
    for (let i = 0; i < 4; i++) {
      const corner = this.corners[i];
      if (!corner) continue;
      const dx = px - corner.x;
      const dy = py - corner.y;
      if (dx * dx + dy * dy <= HANDLE_RADIUS * HANDLE_RADIUS) {
        return `corner-${i}` as DragTarget;
      }
    }
    return null;
  }
}
